// Browser-side live transcription client.
// Talks ONLY to our own backend WebSocket (`/api/live`) with a neutral protocol.
// It has no knowledge of which speech provider sits behind the server.

export interface LiveToken {
  text: string;
  is_final: boolean;
  speaker: string | null;
  start_ms: number | null;
  end_ms: number | null;
}

export type LiveStatus = "idle" | "connecting" | "listening" | "stopped" | "error";

export interface LiveCallbacks {
  onStatus?: (s: LiveStatus) => void;
  onTokens?: (tokens: LiveToken[]) => void;
  onError?: (message: string, code?: string) => void;
}

const TARGET_RATE = 16000;

export class LiveSession {
  private ws: WebSocket | null = null;
  private ctx: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  private stream: MediaStream | null = null;
  private cb: LiveCallbacks;
  private stopped = false;

  constructor(cb: LiveCallbacks) {
    this.cb = cb;
  }

  async start(): Promise<void> {
    this.stopped = false;
    this.cb.onStatus?.("connecting");

    // 1) Microphone.
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
      });
    } catch {
      this.cb.onError?.("Không truy cập được micro", "no_mic");
      this.cb.onStatus?.("error");
      throw new Error("no_mic");
    }
    this.stream = stream;

    // 2) Audio graph (downsample to 16 kHz mono PCM16).
    const AC: typeof AudioContext =
      (window as any).AudioContext || (window as any).webkitAudioContext;
    const ctx = new AC();
    this.ctx = ctx;
    const source = ctx.createMediaStreamSource(stream);
    this.source = source;
    const processor = ctx.createScriptProcessor(4096, 1, 1);
    this.processor = processor;

    // 3) WebSocket to our backend.
    const proto = location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${proto}://${location.host}/api/live`);
    ws.binaryType = "arraybuffer";
    this.ws = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: "start", sampleRate: TARGET_RATE }));
      this.cb.onStatus?.("listening");
    };
    ws.onmessage = (ev) => {
      let msg: any;
      try {
        msg = JSON.parse(typeof ev.data === "string" ? ev.data : "");
      } catch {
        return;
      }
      if (msg?.type === "tokens" && Array.isArray(msg.tokens)) {
        this.cb.onTokens?.(msg.tokens as LiveToken[]);
      } else if (msg?.type === "error") {
        this.cb.onError?.(msg.message ?? "Lỗi dịch vụ", msg.code);
        this.cb.onStatus?.("error");
      } else if (msg?.type === "done") {
        this.cb.onStatus?.("stopped");
      }
    };
    ws.onerror = () => {
      if (!this.stopped) {
        this.cb.onError?.("Mất kết nối máy chủ", "ws_error");
        this.cb.onStatus?.("error");
      }
    };

    const inRate = ctx.sampleRate;
    processor.onaudioprocess = (e) => {
      if (this.stopped || ws.readyState !== WebSocket.OPEN) return;
      const input = e.inputBuffer.getChannelData(0);
      const pcm = downsampleToPCM16(input, inRate, TARGET_RATE);
      if (pcm.byteLength > 0) ws.send(pcm);
    };

    source.connect(processor);
    processor.connect(ctx.destination);
  }

  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    try {
      this.ws?.send(JSON.stringify({ type: "stop" }));
    } catch {
      /* ignore */
    }
    this.processor?.disconnect();
    this.source?.disconnect();
    this.stream?.getTracks().forEach((t) => t.stop());
    this.ctx?.close().catch(() => {});
    setTimeout(() => this.ws?.close(), 400);
    this.cb.onStatus?.("stopped");
  }
}

/** Linear-resample a Float32 buffer and pack to little-endian PCM16. */
function downsampleToPCM16(input: Float32Array, inRate: number, outRate: number): ArrayBuffer {
  let samples: Float32Array;
  if (outRate >= inRate) {
    samples = input;
  } else {
    const ratio = inRate / outRate;
    const outLen = Math.floor(input.length / ratio);
    samples = new Float32Array(outLen);
    for (let i = 0; i < outLen; i++) {
      samples[i] = input[Math.floor(i * ratio)];
    }
  }
  const buf = new ArrayBuffer(samples.length * 2);
  const view = new DataView(buf);
  for (let i = 0; i < samples.length; i++) {
    let s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buf;
}
